import { useEffect, useRef, useState } from "react";
import { AudioLines, Pause, Play, Loader2, RotateCcw, Eye } from "lucide-react";
import { Waveform } from "@/components/Waveform";
import { BrandMark } from "@/components/BrandMark";

const SRC = "/demo-audio/";
// Durations measured from the supplied MP3s with ffprobe (seconds).
const MINE = [
  { id: "m1", from: "Nana", to: "Freddie", title: "The Day We Met", file: "Nana_to_Freddie.mp3", secs: 49.32 },
  { id: "m2", from: "Nana", to: "Sophie", title: "The Day You Found Your Voice", file: "Nana_to_Sophie.mp3", secs: 50.21 },
  { id: "m3", from: "Nana", to: "James", title: "The Phone Call I'll Never Forget", file: "Nana_to_James.mp3", secs: 46.11 },
];
const THEIRS = [
  { id: "t1", from: "Freddie", to: "Nana", title: "My Earliest Memory of Nana", file: "Freddie_to_Nana.mp3", secs: 46.52 },
  { id: "t2", from: "Sophie", to: "Nana", title: "The Thing I Always Remember", file: "Sophie_to_Nana.mp3", secs: 42.45 },
  { id: "t3", from: "James", to: "Nana", title: "Sundays at Your House", file: "James_to_Nana.mp3", secs: 42.37 },
];

const fmt = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

function ChapterRow({ item, active, status, current, duration, onToggle }) {
  const on = active;
  const playing = on && status === "playing";
  const loading = on && status === "loading";
  const failed = on && status === "error";
  const Icon = loading ? Loader2 : playing ? Pause : on && status === "paused" ? Play : AudioLines;
  const total = on && duration ? duration : item.secs;
  const label = `${playing ? "Pause" : on && status === "paused" ? "Resume" : "Play"} ${item.from} to ${item.to}: ${item.title}, ${fmt(total)}`;
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(item)}
        aria-pressed={playing}
        aria-label={label}
        data-testid={`demo-chapter-${item.id}`}
        data-status={on ? status : "idle"}
        className={`w-full text-left rounded-2xl p-4 sm:p-5 flex items-center gap-4 border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2CFF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#120B19] ${
          on ? "bg-[#17171c] border-[rgba(139,44,255,0.6)] shadow-[0_0_28px_rgba(139,44,255,0.12)]" : "bg-[#1A1222] border-[#2E2140] hover:border-[rgba(139,44,255,0.28)]"
        }`}
      >
        <span
          data-testid={`demo-chapter-icon-${item.id}`}
          className={`h-12 w-12 shrink-0 rounded-full flex items-center justify-center ${on && !failed ? "gold-btn" : "border border-[rgba(139,44,255,0.4)] text-[#B57BFF]"}`}
        >
          <Icon className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm gold-text mb-0.5">{item.from} → {item.to}</span>
          <span className="block font-serif text-lg sm:text-xl text-[#EDE6F7] leading-snug truncate" data-testid={`demo-chapter-title-${item.id}`}>{item.title}</span>
          {on && !failed ? (
            <span className="mt-3 block">
              <Waveform bars={28} height={22} active={playing} />
              <span className="mt-2 block h-[3px] w-full rounded-full bg-[#2E2140] overflow-hidden">
                <span className="block h-full bg-[#8B2CFF]" style={{ width: `${Math.min(100, (current / (total || 1)) * 100)}%` }} />
              </span>
            </span>
          ) : (
            <span className="mt-2 block h-[3px] w-full rounded-full bg-[#2E2140]" />
          )}
        </span>
        <span className="shrink-0 text-[#A49CB8] tabular-nums text-sm sm:text-base text-right" data-testid={`demo-chapter-time-${item.id}`}>
          {on && !failed ? <><span className="text-[#EDE6F7]">{fmt(current)}</span> / {fmt(total)}</> : fmt(item.secs)}
        </span>
      </button>
      {failed && (
        <div role="alert" className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-[#5A2440] bg-[#1E1428] px-4 py-2 text-sm text-[#E6CCF2]" data-testid={`demo-chapter-error-${item.id}`}>
          <span>Sorry, this recording couldn't be played.</span>
          <button type="button" onClick={() => onToggle(item, true)} className="inline-flex items-center gap-1 text-[#B57BFF] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2CFF] rounded" data-testid={`demo-chapter-retry-${item.id}`}>
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
          </button>
        </div>
      )}
    </div>
  );
}

function Panel({ eyebrow, heading, items, player }) {
  return (
    <div className="glass-card p-6 sm:p-8">
      <p className="eyebrow mb-1">{eyebrow}</p>
      <h3 className="font-serif text-2xl sm:text-3xl text-[#EDE6F7] mb-6">{heading}</h3>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id}>
            <p className="text-xs uppercase tracking-widest text-[#8C84A0] mb-1.5 pl-1">Chapter {i + 1}</p>
            <ChapterRow item={it} {...player} active={player.active === it.id} onToggle={player.toggle} />
          </div>
        ))}
      </div>
    </div>
  );
}

function useDemoPlayer() {
  const ref = useRef(null);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState("idle");
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const audio = () => {
    if (ref.current) return ref.current;
    const a = new Audio();
    a.preload = "auto";
    a.addEventListener("playing", () => setStatus("playing"));
    a.addEventListener("waiting", () => setStatus("loading"));
    a.addEventListener("pause", () => a.paused && setStatus((s) => (s === "playing" || s === "loading" ? "paused" : s)));
    a.addEventListener("ended", () => { setStatus("ended"); setCurrent(0); });
    a.addEventListener("timeupdate", () => setCurrent(a.currentTime));
    a.addEventListener("loadedmetadata", () => setDuration(a.duration));
    a.addEventListener("error", () => setStatus("error"));
    ref.current = a;
    return a;
  };

  const start = (item) => {
    const a = audio();
    a.pause();
    setActive(item.id);
    setStatus("loading");
    setCurrent(0);
    setDuration(0);
    a.src = SRC + item.file;
    a.currentTime = 0;
    a.play().catch((e) => { if (e?.name !== "AbortError") setStatus("error"); });
  };

  const toggle = (item, restart = false) => {
    const a = ref.current;
    if (restart || !a || active !== item.id || status === "error") return start(item);
    if (status === "playing" || status === "loading") return a.pause();
    if (status === "ended") a.currentTime = 0;
    a.play().catch(() => setStatus("error"));
  };

  useEffect(() => () => ref.current?.pause(), []);
  return { active, status, current, duration, toggle, start };
}

export function DemoStory() {
  const player = useDemoPlayer();
  const first = MINE[0];
  const firstPlaying = player.active === first.id && player.status === "playing";

  return (
    <section className="border-b border-[#2E2140] bg-[#120B19]" data-testid="demo-story-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <p className="eyebrow mb-3">A glimpse</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#EDE6F7] leading-tight">
            What will our Memories Forever actually look like?
          </h2>
          <p className="mt-5 text-lg text-[#C9C2D8] leading-relaxed">
            Two companion stories, side by side — your memories of them, and theirs of you. Tap any chapter to listen
            to an example of how a finished family story is arranged.
          </p>
          <span className="inline-flex items-center gap-2 mt-6 px-4 py-1.5 rounded-full bg-[#1A1222] border border-[rgba(139,44,255,0.3)] text-[#B57BFF] text-xs sm:text-sm tracking-wide" data-testid="demo-badge">
            <AudioLines className="h-4 w-4 shrink-0" aria-hidden="true" /> Illustrative demo recordings — your family's stories use their own recorded voices.
          </span>
        </div>

        <div className="flex items-center justify-center gap-4 my-10" aria-hidden="true">
          <div className="flex-1 max-w-[160px] [mask-image:linear-gradient(to_right,transparent,#0E0A14)]"><Waveform bars={22} height={30} /></div>
          <BrandMark size={56} />
          <div className="flex-1 max-w-[160px] [mask-image:linear-gradient(to_left,transparent,#0E0A14)]"><Waveform bars={22} height={30} /></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Panel eyebrow="Your story" heading="My Memories of You" items={MINE} player={player} />
          <Panel eyebrow="Their story" heading="Your Memories of Me" items={THEIRS} player={player} />
        </div>

        <div className="text-center mt-12">
          <button
            type="button"
            data-testid="hear-example-btn"
            onClick={() => { if (!firstPlaying) player.toggle(first, player.active !== first.id); }}
            aria-label="Preview a family story: play Nana to Freddie, The Day We Met"
            className="gold-btn h-16 px-10 rounded-full text-xl inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDE6F7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#120B19]"
          >
            <Play className="h-6 w-6 mr-2" aria-hidden="true" /> Preview a family story
          </button>
          <p className="mt-4 text-sm text-[#8C84A0]">
            <Eye className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden="true" />
            Nana, Freddie, Sophie and James are fictional characters created for this demonstration.
          </p>
          <p className="sr-only" aria-live="polite" data-testid="demo-live-status">
            {player.active ? `${player.status}` : ""}
          </p>
        </div>
      </div>
    </section>
  );
}
